import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import {
  submitAssistantFeedback,
  type FeedbackSentiment,
} from '@/services/workflowAssistantFeedback';

/** Cap chat history sent with feedback to bound request size and limit the
 *  amount of potentially sensitive content transmitted. */
const MAX_FEEDBACK_CHAT_MESSAGES = 10;

/**
 * Returns a fire-and-forget submitter for assistant thumbs up/down feedback.
 * Attaches the current workflow and the last few chat messages for context (the
 * backend only records non-identifying metadata). Failures are swallowed —
 * feedback must never disrupt the user's flow.
 */
export function useSubmitAssistantFeedback(): (
  sentiment: FeedbackSentiment,
  comment?: string,
) => Promise<void> {
  const { projectName } = useCurrentProject();
  const { workflowName } = useParams<{ workflowName: string }>();
  const { getOttoToken } = useAuth();

  return useCallback(
    async (sentiment: FeedbackSentiment, comment?: string) => {
      if (!projectName || !workflowName) {
        return;
      }

      const workflow = useWorkflowStore.getState().workflow ?? undefined;
      // Only send the most recent messages to bound payload size / exposure.
      const chatHistory = useAgentChatAguiStore
        .getState()
        .messageHistory.slice(-MAX_FEEDBACK_CHAT_MESSAGES)
        .map((m) => ({ role: m.role, content: m.content }));

      try {
        await submitAssistantFeedback({
          projectName,
          workflowName,
          getToken: getOttoToken,
          feedback: {
            sentiment,
            ...(comment ? { comment } : {}),
            ...(workflow ? { workflow } : {}),
            ...(chatHistory.length ? { chatHistory } : {}),
          },
        });
      } catch (error) {
        console.warn('[assistant] Failed to submit feedback', error);
      }
    },
    [projectName, workflowName, getOttoToken],
  );
}
